import { useEffect, useMemo, useState } from "react";
import { useApp } from "../../store.jsx";
import { Icon } from "../Common.jsx";
import StarRating from "./StarRating.jsx";
import { listTeam, createReview, getReviewPeriodSummary } from "../../lib/api";

const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];
const MONTH_SHORT = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Client-side mirror of backend normalizePeriod — for the live preview + the
// report-evidence query. Returns { label, start, end } (ISO) or null.
function computePeriod(type, v) {
  try {
    if (type === "monthly") {
      if (!v.year || !v.month) return null;
      return {
        label: `${MONTH_SHORT[v.month - 1]} ${v.year}`,
        start: new Date(Date.UTC(v.year, v.month - 1, 1)).toISOString(),
        end: new Date(Date.UTC(v.year, v.month, 0)).toISOString(),
      };
    }
    if (type === "quarterly") {
      if (!v.year || !v.quarter) return null;
      const sm = (v.quarter - 1) * 3;
      return {
        label: `Q${v.quarter} ${v.year}`,
        start: new Date(Date.UTC(v.year, sm, 1)).toISOString(),
        end: new Date(Date.UTC(v.year, sm + 3, 0)).toISOString(),
      };
    }
    if (type === "annual") {
      if (!v.year) return null;
      return {
        label: `${v.year}`,
        start: new Date(Date.UTC(v.year, 0, 1)).toISOString(),
        end: new Date(Date.UTC(v.year, 12, 0)).toISOString(),
      };
    }
    if (type === "custom") {
      if (!v.start || !v.end || v.end < v.start) return null;
      return {
        label: `${v.start} → ${v.end}`,
        start: new Date(`${v.start}T00:00:00.000Z`).toISOString(),
        end: new Date(`${v.end}T00:00:00.000Z`).toISOString(),
      };
    }
  } catch {
    return null;
  }
  return null;
}

const ERR_MAP = {
  duplicate_review_for_period: "A review already exists for this employee and period.",
  cannot_self_review: "You can't review yourself.",
  employee_out_of_scope: "That employee isn't in your team.",
  invalid_rating: "Please choose a rating between 1 and 5.",
  invalid_period: "Please complete the period fields.",
  invalid_input: "Please check the form and try again.",
  user_not_found: "That employee no longer exists.",
};

function Metric({ label, value }) {
  return (
    <div>
      <div style={{ color: "var(--text-muted)", fontSize: 11 }}>{label}</div>
      <div style={{ fontWeight: 600 }}>{value}</div>
    </div>
  );
}

export default function ReviewFormModal({ meta, onSaved }) {
  const { closeModal, toast } = useApp();
  const ratingLabels = meta?.ratingLabels || {};
  const periodTypes = meta?.periodTypes || [{ value: "monthly", label: "Monthly" }];

  const now = new Date();
  const curMonthNum = now.getMonth() + 1;
  let prevMonth = curMonthNum - 1;
  let prevMonthYear = now.getFullYear();
  if (prevMonth === 0) {
    prevMonth = 12;
    prevMonthYear -= 1;
  }
  const curQuarter = Math.ceil(curMonthNum / 3);
  const defQuarter = curQuarter === 1 ? 4 : curQuarter - 1;

  const [employees, setEmployees] = useState([]);
  const [employeeId, setEmployeeId] = useState("");
  const [periodType, setPeriodType] = useState("monthly");
  const [year, setYear] = useState(prevMonthYear);
  const [month, setMonth] = useState(prevMonth);
  const [quarter, setQuarter] = useState(defQuarter);
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");

  const [rating, setRating] = useState(0);
  const [strengths, setStrengths] = useState("");
  const [improvements, setImprovements] = useState("");
  const [feedback, setFeedback] = useState("");

  const [summary, setSummary] = useState(null);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  useEffect(() => {
    listTeam()
      .then((d) => setEmployees(d.users || []))
      .catch(() => {});
  }, []);

  const period = useMemo(
    () =>
      computePeriod(periodType, {
        year: Number(year),
        month: Number(month),
        quarter: Number(quarter),
        start,
        end,
      }),
    [periodType, year, month, quarter, start, end],
  );

  // Fetch report evidence once employee + a valid period are chosen.
  useEffect(() => {
    setSummary(null);
    if (!employeeId || !period) return undefined;
    let cancelled = false;
    getReviewPeriodSummary({ employeeId, periodStart: period.start, periodEnd: period.end })
      .then((d) => {
        if (!cancelled) setSummary(d.summary);
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [employeeId, period]);

  const buildPayload = (status) => {
    const base = { employeeId, periodType, overallRating: rating, status };
    if (strengths.trim()) base.strengths = strengths.trim();
    if (improvements.trim()) base.improvements = improvements.trim();
    if (feedback.trim()) base.feedback = feedback.trim();
    if (periodType === "monthly") return { ...base, year: Number(year), month: Number(month) };
    if (periodType === "quarterly") return { ...base, year: Number(year), quarter: Number(quarter) };
    if (periodType === "annual") return { ...base, year: Number(year) };
    return { ...base, start, end };
  };

  const submit = async (status) => {
    setErr("");
    if (!employeeId) return setErr("Please select an employee.");
    if (!period) return setErr("Please complete the period fields.");
    if (!rating) return setErr("Please choose an overall rating.");
    setSaving(true);
    try {
      await createReview(buildPayload(status));
      toast(status === "published" ? "Review published" : "Draft saved", "success");
      closeModal();
      onSaved?.();
    } catch (e) {
      setErr(ERR_MAP[e.message] || e.message || "Could not save review.");
      setSaving(false);
    }
  };

  const years = [];
  for (let y = now.getFullYear(); y >= now.getFullYear() - 3; y--) years.push(y);

  return (
    <>
      <div className="modal-head">
        <h3>New performance review</h3>
        <button className="icon-btn" onClick={closeModal}>
          <Icon name="x" size={16} />
        </button>
      </div>
      <div className="modal-body" style={{ display: "flex", flexDirection: "column", gap: 14 }}>
        {err && (
          <div className="badge badge-error" style={{ padding: "8px 12px", whiteSpace: "normal" }}>
            {err}
          </div>
        )}

        <div className="field">
          <label>Employee *</label>
          <select className="input" value={employeeId} onChange={(e) => setEmployeeId(e.target.value)}>
            <option value="">Select employee…</option>
            {employees.map((u) => (
              <option key={u.id} value={u.id}>
                {u.name}
                {u.department ? ` — ${u.department}` : ""}
              </option>
            ))}
          </select>
        </div>

        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
          <div className="field" style={{ minWidth: 130 }}>
            <label>Period</label>
            <select className="input" value={periodType} onChange={(e) => setPeriodType(e.target.value)}>
              {periodTypes.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>
          </div>
          {periodType !== "custom" && (
            <div className="field" style={{ minWidth: 100 }}>
              <label>Year</label>
              <select className="input" value={year} onChange={(e) => setYear(e.target.value)}>
                {years.map((y) => (
                  <option key={y} value={y}>
                    {y}
                  </option>
                ))}
              </select>
            </div>
          )}
          {periodType === "monthly" && (
            <div className="field" style={{ minWidth: 130 }}>
              <label>Month</label>
              <select className="input" value={month} onChange={(e) => setMonth(e.target.value)}>
                {MONTHS.map((m, i) => (
                  <option key={m} value={i + 1}>
                    {m}
                  </option>
                ))}
              </select>
            </div>
          )}
          {periodType === "quarterly" && (
            <div className="field" style={{ minWidth: 100 }}>
              <label>Quarter</label>
              <select className="input" value={quarter} onChange={(e) => setQuarter(e.target.value)}>
                {[1, 2, 3, 4].map((q) => (
                  <option key={q} value={q}>
                    Q{q}
                  </option>
                ))}
              </select>
            </div>
          )}
          {periodType === "custom" && (
            <>
              <div className="field" style={{ minWidth: 140 }}>
                <label>Start</label>
                <input type="date" className="input" value={start} onChange={(e) => setStart(e.target.value)} />
              </div>
              <div className="field" style={{ minWidth: 140 }}>
                <label>End</label>
                <input type="date" className="input" value={end} onChange={(e) => setEnd(e.target.value)} />
              </div>
            </>
          )}
        </div>
        <div style={{ fontSize: 12.5, color: "var(--text-muted)" }}>
          Period: <strong style={{ color: "var(--text)" }}>{period ? period.label : "—"}</strong>
        </div>

        {summary && (
          <div className="card" style={{ padding: 12, background: "var(--surface-2)", fontSize: 12.5 }}>
            <div style={{ fontWeight: 600, marginBottom: 8, display: "flex", alignItems: "center", gap: 6 }}>
              <Icon name="sparkles" size={14} /> Report evidence for this period
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(120px,1fr))", gap: 8 }}>
              <Metric label="Reports" value={`${summary.reportCount} (${summary.compliancePct}%)`} />
              <Metric
                label="Avg productivity"
                value={summary.avgProductivity != null ? `${summary.avgProductivity}%` : "—"}
              />
              <Metric label="Tasks done" value={`${summary.tasks.done}/${summary.tasks.total}`} />
              <Metric
                label="Sentiment"
                value={`${summary.sentiment.positive}+ / ${summary.sentiment.negative}−`}
              />
            </div>
            {summary.topBlockers?.length > 0 && (
              <div style={{ marginTop: 8, color: "var(--text-muted)" }}>
                Top blockers: {summary.topBlockers.slice(0, 3).map((b) => b.text).join("; ")}
              </div>
            )}
            {summary.projects?.length > 0 && (
              <div style={{ marginTop: 6, color: "var(--text-muted)" }}>
                Projects advanced: {summary.projects.map((p) => `${p.name} (${p.completed})`).join(", ")}
              </div>
            )}
          </div>
        )}

        <div className="field">
          <label>Overall rating *</label>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <StarRating value={rating} labels={ratingLabels} onChange={setRating} size={22} />
            <span style={{ fontSize: 13, color: "var(--text-muted)" }}>
              {rating ? ratingLabels[rating] || `${rating}/5` : "Not rated"}
            </span>
          </div>
        </div>

        <div className="field">
          <label>Strengths</label>
          <textarea className="input" rows={2} value={strengths} onChange={(e) => setStrengths(e.target.value)} maxLength={5000} />
        </div>
        <div className="field">
          <label>Areas to improve</label>
          <textarea className="input" rows={2} value={improvements} onChange={(e) => setImprovements(e.target.value)} maxLength={5000} />
        </div>
        <div className="field">
          <label>Feedback</label>
          <textarea className="input" rows={3} value={feedback} onChange={(e) => setFeedback(e.target.value)} maxLength={10000} />
        </div>
      </div>
      <div className="modal-foot">
        <button className="btn btn-ghost" onClick={closeModal} disabled={saving}>
          Cancel
        </button>
        <button className="btn btn-ghost" onClick={() => submit("draft")} disabled={saving}>
          Save draft
        </button>
        <button className="btn btn-primary" onClick={() => submit("published")} disabled={saving}>
          {saving ? "Saving…" : "Publish"}
        </button>
      </div>
    </>
  );
}
